import { cn } from "@ui/utils"
import type { LucideIcon } from "lucide-react"

const RADIUS = 42
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const SPINNER_ARC = CIRCUMFERENCE * 0.25
// Slower than the default animate-spin (1s), which reads as anxious for a background sync.
const SPIN_DURATION = "2s"

export type RingMode = "complete" | "spinner" | "idle"

interface BackfillRingProps {
  mode: RingMode
  colorClassName: string
  icon: LucideIcon
  className?: string
}

// A small donut with a centered icon. Mirrors the SVG ring approach in
// components/scouting/match-score-chart.tsx, with a few modes:
//   complete  – full ring, static (that phase is done)
//   spinner   – indeterminate arc that rotates while the phase is active
//   idle      – muted track only while the phase waits for a prerequisite
export function BackfillRing({ mode, colorClassName, icon: Icon, className }: BackfillRingProps) {
  const isSpinner = mode === "spinner"
  const isIdle = mode === "idle"

  return (
    <div className={cn("relative size-5 flex-none", className)}>
      <svg
        className={cn("size-full", isSpinner ? "animate-spin" : "-rotate-90")}
        style={isSpinner ? { animationDuration: SPIN_DURATION } : undefined}
        viewBox="0 0 100 100"
      >
        <circle cx="50" cy="50" r={RADIUS} stroke="currentColor" strokeWidth="8" fill="none" className="text-muted" />
        {isSpinner && (
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${SPINNER_ARC} ${CIRCUMFERENCE - SPINNER_ARC}`}
            className={colorClassName}
          />
        )}
        {!isSpinner && !isIdle && (
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            className={colorClassName}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon className={cn("size-2.5", isIdle ? "text-muted-foreground" : colorClassName)} />
      </div>
    </div>
  )
}
